import {
  type ApplicationConfig,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
  inject,
} from '@angular/core';
import { provideRouter, TitleStrategy, withComponentInputBinding } from '@angular/router';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptors,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { providePrimeNG } from 'primeng/config';
import { MsalBroadcastService, MsalGuard, MsalInterceptor, MsalService } from '@azure/msal-angular';
import { firstValueFrom } from 'rxjs';

import { environment } from '../environments/environment';
import { MomentumTitleStrategy } from './core/momentum-title.strategy';
import { APP_ENV } from './core/tokens/app-environment.token';
import { routes } from './app.routes';
import { zuehlkePreset } from './core/theme/zuehlke.preset';
import { mockApiInterceptor } from './core/interceptors/mock-api.interceptor';
import { provideMsalAuth } from './core/auth/msal.config';

const authEnabled = environment.auth.enabled && !!environment.auth.clientId;

const authProviders = authEnabled
  ? [
      ...provideMsalAuth(environment.auth),
      MsalService,
      MsalGuard,
      MsalBroadcastService,
      { provide: HTTP_INTERCEPTORS, useClass: MsalInterceptor, multi: true },
      provideAppInitializer(() => {
        const msal = inject(MsalService);
        return firstValueFrom(msal.handleRedirectObservable());
      }),
    ]
  : [];

export const appConfig: ApplicationConfig = {
  providers: [
    { provide: APP_ENV, useValue: environment },
    { provide: TitleStrategy, useClass: MomentumTitleStrategy },
    provideBrowserGlobalErrorListeners(),
    provideZonelessChangeDetection(),
    provideRouter(routes, withComponentInputBinding()),
    provideHttpClient(
      withInterceptorsFromDi(),
      withInterceptors([
        ...(Object.values(environment.enableMockApi).some(Boolean) ? [mockApiInterceptor] : []),
      ]),
    ),
    ...authProviders,
    provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        preset: zuehlkePreset,
        options: {
          darkModeSelector: '.dark-mode',
        },
      },
    }),
  ],
};
